// Argus — DrivesView: vizualizare dedicată Windows-style pentru drives root (parent='').
// Independent de view mode (details/list/thumbnails). 2 secțiuni: Devices and drives + Network locations.
// Card per drive: icon + label + LinearProgress free/used + text "X GB free of Y GB".

import { useEffect, useRef } from 'react';
import { Box, Card, LinearProgress, Typography } from '@mui/material';
import { formatEntryDisplayName, type FsEntry } from '../../lib/fsEntries';
import { formatBytes } from '../../lib/dirListing';
import { EntryIcon } from './EntryIcon';

export interface DrivesViewProps {
  entries: readonly FsEntry[];
  agentId: string;
  selectedEntryName?: string | null;
  onSelect?: ((entry: FsEntry | null) => void) | undefined;
  onOpenDrive: (name: string) => void;
}

function DriveCard({ entry, agentId, isSelected, onSelect, onOpenDrive }: { entry: FsEntry; agentId: string; isSelected: boolean; onSelect: (entry: FsEntry) => void; onOpenDrive: (name: string) => void }): JSX.Element {
  const free = entry.freeBytes ?? 0;
  const total = entry.totalBytes ?? 0;
  const used = total > 0 ? total - free : 0;
  const usedPct = total > 0 ? Math.min(100, Math.max(0, (used / total) * 100)) : 0;

  // Color thumb pe nivel ocupare: verde <70%, oranj <90%, roșu >=90%.
  const progressColor: 'primary' | 'warning' | 'error' =
    usedPct >= 90 ? 'error' : usedPct >= 70 ? 'warning' : 'primary';

  return (
    <Card
      data-drive-name={entry.name}
      onDoubleClick={() => onOpenDrive(entry.name)}
      onClick={() => onSelect(entry)}
      sx={{
        width: 260,
        p: 1.5,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 1.5,
        cursor: 'pointer',
        border: 1,
        borderColor: isSelected ? 'primary.main' : 'divider',
        backgroundColor: isSelected ? 'action.selected' : undefined,
        transition: 'border-color 120ms, transform 120ms, background-color 120ms',
        '&:hover': { borderColor: 'primary.main', transform: 'translateY(-1px)' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <EntryIcon entry={entry} agentId={agentId} size={48} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
          {formatEntryDisplayName(entry)}
        </Typography>
        {entry.driveType === 'network' ? (
          <Typography variant="caption" sx={{ color: 'text.disabled', lineHeight: 1.2 }} noWrap>
            {entry.name}
          </Typography>
        ) : null}
        {total > 0 ? (
          <>
            <LinearProgress
              variant="determinate"
              value={usedPct}
              color={progressColor}
              sx={{ height: 6, borderRadius: 1 }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {formatBytes(free)} free of {formatBytes(total)}
            </Typography>
          </>
        ) : (
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            Capacity unknown
          </Typography>
        )}
      </Box>
    </Card>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 1, ml: 0.5 }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1.5 }}>
        {children}
      </Box>
    </Box>
  );
}

export function DrivesView({ entries, agentId, selectedEntryName = null, onSelect, onOpenDrive }: DrivesViewProps): JSX.Element {
  const localDrives = entries.filter((e) => e.driveType !== 'network');
  const networkDrives = entries.filter((e) => e.driveType === 'network');
  const handleSelect = (entry: FsEntry) => onSelect?.(entry);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!selectedEntryName) return;
    const container = containerRef.current;
    if (container === null) return;
    let rafId: number | null = null;
    let elapsed = 0;
    const tryScroll = () => {
      const safeName = selectedEntryName.replace(/"/g, '\\"');
      const el = container.querySelector(`[data-drive-name="${safeName}"]`) as HTMLElement | null;
      if (el !== null) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return;
      }
      elapsed += 16;
      if (elapsed < 1000) rafId = window.requestAnimationFrame(tryScroll);
    };
    rafId = window.requestAnimationFrame(tryScroll);
    return () => { if (rafId !== null) window.cancelAnimationFrame(rafId); };
  }, [selectedEntryName, entries]);

  return (
    <Box ref={containerRef} sx={{ flexGrow: 1, overflow: 'auto', p: 2 }} onClick={(e) => { if (e.target === e.currentTarget) onSelect?.(null); }}>
      {localDrives.length > 0 ? (
        <Section title="Devices and drives">
          {localDrives.map((d) => <DriveCard key={d.name} entry={d} agentId={agentId} isSelected={d.name === selectedEntryName} onSelect={handleSelect} onOpenDrive={onOpenDrive} />)}
        </Section>
      ) : null}
      {networkDrives.length > 0 ? (
        <Section title="Network locations">
          {networkDrives.map((d) => <DriveCard key={d.name} entry={d} agentId={agentId} isSelected={d.name === selectedEntryName} onSelect={handleSelect} onOpenDrive={onOpenDrive} />)}
        </Section>
      ) : null}
      {entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
          No drives available.
        </Typography>
      ) : null}
    </Box>
  );
}
